import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { UnitDocType, UnitType } from '@lancar-pos/db-schema';

export type Unit = UnitDocType;

const DEFAULT_UNITS: Omit<Unit, 'id' | 'createdAt' | 'updatedAt' | '_deleted'>[] = [
  { name: 'Pieces', abbreviation: 'pcs', type: 'count', isDefault: true },
  { name: 'Kilogram', abbreviation: 'kg', type: 'weight', isDefault: false },
  { name: 'Gram', abbreviation: 'g', type: 'weight', isDefault: false },
  { name: 'Liter', abbreviation: 'L', type: 'volume', isDefault: false },
  { name: 'Milliliter', abbreviation: 'mL', type: 'volume', isDefault: false },
  { name: 'Meter', abbreviation: 'm', type: 'length', isDefault: false },
  { name: 'Centimeter', abbreviation: 'cm', type: 'length', isDefault: false },
  { name: 'Box', abbreviation: 'box', type: 'count', isDefault: false },
  { name: 'Pack', abbreviation: 'pack', type: 'count', isDefault: false },
  { name: 'Dozen', abbreviation: 'lusin', type: 'count', isDefault: false },
];

export function useUnitStore() {
  const db = useDatabase();
  const [units, setUnits] = useState<Unit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.units
      .find({
        selector: { _deleted: false },
        sort: [{ isDefault: 'desc' }, { name: 'asc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => Unit }>) => {
        setUnits(docs.map((doc: { toMutableJSON: () => Unit }) => doc.toMutableJSON() as Unit));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.units]);

  const initializeDefaultUnits = useCallback(async () => {
    const existingUnits = await db.units.find().exec();
    if (existingUnits.length === 0) {
      const now = new Date().toISOString();
      await db.units.bulkInsert(
        DEFAULT_UNITS.map((unit, index) => ({
          ...unit,
          id: `unit-${index + 1}`,
          createdAt: now,
          updatedAt: now,
          _deleted: false,
        }))
      );
      console.log('Initialized default units');
    }
  }, [db.units]);

  const addUnit = useCallback(
    async (unit: Omit<Unit, 'id' | 'createdAt' | 'updatedAt' | '_deleted'>) => {
      const now = new Date().toISOString();
      await db.units.insert({
        ...unit,
        id: crypto.randomUUID(),
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      });
    },
    [db.units]
  );

  const updateUnit = useCallback(
    async (id: string, updates: Partial<Omit<Unit, 'id' | 'createdAt'>>) => {
      const doc = await db.units.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.units]
  );

  const deleteUnit = useCallback(
    async (id: string) => {
      const doc = await db.units.findOne(id).exec();
      if (doc) {
        await doc.patch({
          _deleted: true,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.units]
  );

  const getUnitById = useCallback(
    (id: string): Unit | undefined => {
      return units.find((u) => u.id === id);
    },
    [units]
  );

  const getUnitsByType = useCallback(
    (type: UnitType): Unit[] => {
      return units.filter((u) => u.type === type);
    },
    [units]
  );

  return {
    units,
    isLoading,
    initializeDefaultUnits,
    addUnit,
    updateUnit,
    deleteUnit,
    getUnitById,
    getUnitsByType,
  };
}

export type UnitStore = ReturnType<typeof useUnitStore>;
